import { createEncodedFilter, createEncodedParameters, generateRandom } from "./SaitUtils";
import { activateLoading, deactivateLoading } from "../store/globalLoadingReducer";
import i18next from "i18next";
import { setGlobalError, setGlobalMessage } from "../store/globalMessageReducer";
import { downloadFile, getBlobRtf, getBlobImage, getBlobPDF, getBlobXls, getBlobXlsx } from "./FileUtils";
import { configManager } from "./ConfigManager";

export function getNewBackendUrl(customBackendContextPath) {
	return (
		window["Configs"].newBackendAddress ||
		`${window["Configs"].newBackendProtocol}://${window.location.hostname}:${window["Configs"].newBackendPort}/${customBackendContextPath || "jsws-2-backend"}/`
	);
}

export function getOldBackendUrl() {
	let context = window["Configs"].oldBackendContext || "JSWS";
	return window["Configs"].oldBackendAddress || `${window["Configs"].oldBackendProtocol}://${window.location.hostname}:${window["Configs"].oldBackendPort}/${context}/`;
}

export async function axiosGetForGrid(findFunction, jsonFilter, orderBy, pageSize, pageNumber, token, module, dispatch, customAxiosInstance) {
	const axiosInstance = customAxiosInstance?.instance || configManager.getConfig("axiosInstance");
	const jwtToken = customAxiosInstance?.token ? localStorage.getItem(customAxiosInstance.token) : token;
	try {
		const response = await axiosInstance.get(findFunction, {
			headers: {
				Authorization: `Bearer ${jwtToken}`,
			},
			params: {
				filters: createEncodedFilter(jsonFilter, orderBy, pageSize, pageNumber),
				module: module,
			},
		});
		let rowsResp = {
			rows: [],
			totalCount: 0,
		};
		if (response?.data) {
			if (response.data.items) {
				rowsResp.rows = response.data.items?.map((row) => {
					row.id = generateRandom();
					return row;
				});
				rowsResp.totalCount = response.data.totalCount;

				if (rowsResp.rows) {
					if (rowsResp.rows.length === 0 && rowsResp.totalCount > 0) {
						dispatch(
							setGlobalError({
								module: module,
								errorMessage: i18next.t("error_mapping_find"),
							}),
						);
					}
				}
			} else {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t("error_find"),
					}),
				);
			}
		}

		return rowsResp;
	} catch (error) {
		console.error(error);
		return {
			rows: [],
			totalCount: 0,
		};
	}
}

export async function axiosGet(findFunction, jsonFilter, orderBy) {
	try {
		const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
		const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
		let axiosInstance = configManager.getConfig("axiosInstance");
		const response = await axiosInstance.get(findFunction, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				filters: createEncodedFilter(jsonFilter || [], orderBy || [{ property: "1", direction: "ASC" }], 1000, 0),
				module: module,
			},
		});
		let rowsResp = {
			rows: [],
			totalCount: 0,
		};
		if (response?.data) {
			rowsResp.rows = response.data.items?.map((row) => {
				row.id = generateRandom();
				return row;
			});
			rowsResp.totalCount = response.data.totalCount;
		}

		return rowsResp;
	} catch (error) {
		console.error(error);
		return {
			rows: [],
			totalCount: 0,
		};
	}
}

export async function axiosPost(procedureName, parameters, customAxiosInstance) {
	var resp = {
		success: false,
		message: "",
	};

	try {
		const token = localStorage.getItem(customAxiosInstance?.token || configManager.getConfig("localStorageToken"));
		const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
		let axiosInstance = customAxiosInstance?.instance || configManager.getConfig("axiosInstance");
		const response = await axiosInstance.post(
			procedureName,
			{ parameters: createEncodedParameters(parameters), module: module },
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);

		if (response.status === 200) {
			if (response.data.retval === 0) {
				resp = { ...resp, ...response.data };
				resp.success = true;
				resp.message = "Retval: " + response.data.retval + " --- Messaggio: " + response.data.messaggio;
			} else {
				resp.success = false;
				let errorMessage = response.data.messaggio;
				const dbError = /(^[A-Z0-9_]+): (-[0-9]+) - /g.exec(errorMessage); //Esempio: IWSMHLDCAL_PACK: -1401 -
				if (dbError) {
					errorMessage = errorMessage.replace(dbError[0], "");
					resp.title =
						i18next.t("error_code") +
						": " +
						response.data.retval +
						" (" +
						dbError[1] + //Nome Package
						")";
				}
				resp.message = errorMessage;
			}
		} else {
			resp.success = false;
			resp.message = "ERROR CODE " + response.status;
		}

		return resp;
	} catch (error) {
		resp.message = "Exception Post";
		if (error?.response?.data) {
			if (typeof error.response.data === "string") {
				resp.message = error.response.data;
			} else {
				if (typeof error.response.data.error === "string") {
					resp.message = error.response.data.error;
				}
			}
		} else if (error?.message) {
			resp.message = error.message;
		}
		return resp;
	}
}

export async function standardAxiosPost(params) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));

	params.dispatch(activateLoading(module));
	try {
		const token = localStorage.getItem(params.customAxiosInstance?.token || configManager.getConfig("localStorageToken"));
		let axiosInstance = params.customAxiosInstance?.instance || configManager.getConfig("axiosInstance");
		const response = await axiosInstance.post(
			params.procedureName,
			{ parameters: createEncodedParameters(params.parameters), module: module },
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);

		if (response.status === 200) {
			if (response.data.retval === 0) {
				params.dispatch(deactivateLoading(module));
				params.onSuccess?.(response.data);
			} else {
				let errorMessage = response.data.messaggio;
				let title;
				const dbError = /(^[A-Z0-9_]+): (-[0-9]+) - /g.exec(errorMessage); //Esempio: IWSMHLDCAL_PACK: -1401 -
				if (dbError) {
					errorMessage = errorMessage.replace(dbError[0], "");
					title =
						i18next.t("error_code") +
						": " +
						response.data.retval +
						" (" +
						dbError[1] + //Nome Package
						")";
				}
				params.dispatch(
					setGlobalError({
						module: module,
						title: title,
						errorMessage: errorMessage,
					}),
				);
				params.dispatch(deactivateLoading(module));
				params.onError?.(response.data);
			}
		} else {
			let errorMessage = "ERROR CODE " + response.status;
			params.dispatch(
				setGlobalError({
					module: module,
					errorMessage: errorMessage,
				}),
			);
			params.dispatch(deactivateLoading(module));
			params.onError?.(response.data);
		}
	} catch (error) {
		let errorMessage = "Exception Post";
		if (error?.response?.data) {
			if (typeof error.response.data === "string") {
				errorMessage = error.response.data;
			} else {
				if (typeof error.response.data.error === "string") {
					errorMessage = error.response.data.error;
				}
			}
		} else if (error.message) {
			errorMessage = error.message;
		}
		params.dispatch(
			setGlobalError({
				module: module,
				errorMessage: errorMessage,
			}),
		);
		params.dispatch(deactivateLoading(module));
		const errResp = {
			messaggio: "ERROR",
			retval: -909090,
		};
		params.onError?.(errResp);
	}
}

export async function standardAxiosGet(params) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));

	params.dispatch(activateLoading(module));
	try {
		const token = localStorage.getItem(params.customAxiosInstance?.token || configManager.getConfig("localStorageToken"));
		let axiosInstance = params.customAxiosInstance?.instance || configManager.getConfig("axiosInstance");
		const response = await axiosInstance.get(params.findFunction, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				filters: createEncodedFilter(params.jsonFilter || [], params.orderBy || [{ property: "1", direction: "ASC" }], 1000, 0),
				module: module,
			},
		});

		let rowsResp = {
			rows: [],
			totalCount: 0,
		};
		if (response?.data) {
			rowsResp.rows = response.data.items?.map((row) => {
				row.id = generateRandom();
				return row;
			});
			rowsResp.totalCount = response.data.totalCount;
		}

		params.dispatch(deactivateLoading(module));
		return rowsResp;
	} catch (error) {
		console.error(error);
		params.dispatch(deactivateLoading(module));
		return {
			rows: [],
			totalCount: 0,
		};
	}
}

export function axiosFetchAllCombo(comboQueries, customAxiosInstance) {
	comboQueries.forEach((comboQuery) => {
		axiosGetForCombo(comboQuery.findFunction, comboQuery.findFilters, comboQuery.orderBy, comboQuery.setItems, customAxiosInstance);
	});
}

export function axiosGetForCombo(findFunction, jsonFilter, orderBy, setItems, customAxiosInstance) {
	const token = localStorage.getItem(customAxiosInstance?.token || configManager.getConfig("localStorageToken"));
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	let axiosInstance = customAxiosInstance?.instance || configManager.getConfig("axiosInstance");
	return axiosInstance
		.get(findFunction, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				filters: createEncodedFilter(jsonFilter || [], orderBy || [{ property: "1", direction: "ASC" }], 1000, 0),
				module: module,
			},
		})
		.then((result) => {
			if (result?.data) {
				let items = result.data.items?.map((row) => {
					row.id = generateRandom();
					return row;
				});
				setItems(items);
			} else {
				setItems([]);
			}
		})
		.catch((error) => {
			console.error(error);
			setItems([]);
		});
}

export async function previewReport(dispatch, nomeReport, paramsList, format) {
	if (!format) {
		format = ".pdf";
	}

	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const response = await axiosInstance.get("./downloadReportJavascript", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				reportName: nomeReport,
				parametersList: encodeURIComponent(paramsList),
				module: module,
			},
		});

		if (response["status"] === 200) {
			var responseJson = response["data"];
			if (responseJson.retval >= 0) {
				let responseReport = responseJson.messaggio;
				if (!responseReport) {
					dispatch(
						setGlobalError({
							module: module,
							errorMessage: i18next.t("error_impossible_open_file"),
						}),
					);
				} else {
					var file;
					if (format === ".pdf") {
						file = getBlobPDF(responseReport);
						let fileURL = URL.createObjectURL(file);
						window.open(fileURL);
					} else {
						if (format === ".xlsx") {
							file = getBlobXlsx(responseReport);
						} else if (format === ".xls") {
							file = getBlobXls(responseReport);
						} else if (format === ".rtf") {
							file = getBlobRtf(responseReport);
						}
						downloadFile(file, nomeReport);
					}
				}
			} else {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: responseJson.messaggio,
					}),
				);
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: i18next.t("failed_connection"),
			}),
		);
	}
	dispatch(deactivateLoading(module));
}

export async function downloadReport(dispatch, nomeReport, paramsList, format, customReportName) {
	if (!format) {
		format = ".pdf";
	}

	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const response = await axiosInstance.get("./downloadReportJavascript", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				reportName: nomeReport,
				parametersList: encodeURIComponent(paramsList),
				module: module,
			},
		});

		if (response["status"] === 200) {
			var responseJson = response["data"];
			if (responseJson.retval >= 0) {
				var responseReport = responseJson.messaggio;
				if (!responseReport) {
					dispatch(
						setGlobalError({
							module: module,
							errorMessage: i18next.t("error_impossible_open_file"),
						}),
					);
				} else {
					var file;
					if (format === ".pdf") {
						file = getBlobPDF(responseReport);
					} else if (format === ".xlsx") {
						file = getBlobXlsx(responseReport);
					} else if (format === ".xls") {
						file = getBlobXls(responseReport);
					} else if (format === ".rtf") {
						file = getBlobRtf(responseReport);
					}
					downloadFile(file, customReportName || nomeReport);
				}
			} else {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: responseJson.messaggio,
					}),
				);
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: i18next.t("failed_connection"),
			}),
		);
	}
	dispatch(deactivateLoading(module));
}

export async function sendReportMail(dispatch, filters, paramsList) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const response = await axiosInstance.get("./sendReportMail", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				filters: encodeURIComponent(JSON.stringify(filters)),
				parametersList: encodeURIComponent(paramsList),
				module: module,
			},
		});

		if (response["status"] === 200) {
			const responseJson = response["data"];
			const idJob = responseJson.messaggio;

			if (responseJson.retval === 0) {
				// Creato e inviato
				dispatch(
					setGlobalMessage({
						module: module,
						title: i18next.t("sending_successful"),
						message: i18next.t("report_correctly_sent", {
							jobId: idJob,
						}),
					}),
				);
			} else if (responseJson.retval === -3) {
				// Creato e blocato
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t("report_sending_blocked", {
							jobId: idJob,
						}),
					}),
				);
			} else if (responseJson.retval === -4) {
				// Creato e in errore
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t("error_sending_report", {
							jobId: idJob,
						}),
					}),
				);
			} else {
				// Non Creato
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: responseJson.messaggio,
					}),
				);
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: i18next.t("failed_connection"),
			}),
		);
	}
	dispatch(deactivateLoading(module));
}

export async function openDocument(dispatch, path) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const response = await axiosInstance.get("./fileServerWS/read", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				path: encodeURIComponent(path),
			},
		});

		if (response["status"] === 200) {
			var responseFile = response["data"];
			if (!responseFile) {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t("error_impossible_open_file"),
					}),
				);
			} else {
				let fileExtension = path.split(".").pop().toLowerCase();
				if (fileExtension === "pdf") {
					var file = getBlobPDF(responseFile);
					var fileURL = URL.createObjectURL(file);
					window.open(fileURL);
				} else {
					let contentType = "";

					if (fileExtension === "jpg" || fileExtension === "jpeg") {
						contentType = "image/jpeg";
					} else if (fileExtension === "png") {
						contentType = "image/png";
					} else {
						contentType = "image/jpeg";
					}

					const blob = getBlobImage(contentType, responseFile);
					const blobUrl = URL.createObjectURL(blob);

					window.open(blobUrl, "_blank");
				}
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: i18next.t("failed_connection"),
			}),
		);
	}

	dispatch(deactivateLoading(module));
}

export async function downloadDocument(dispatch, path, fileName) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const response = await axiosInstance.get("./fileServerWS/read", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				path: encodeURIComponent(path),
			},
		});

		if (response["status"] === 200) {
			var responseFile = response["data"];
			if (!responseFile) {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t("error_impossible_open_file"),
					}),
				);
			} else {
				var blob;

				let fileExtension = path.split(".").pop().toLowerCase();
				if (fileExtension === "pdf") {
					blob = getBlobPDF(responseFile);
				} else if (fileExtension === "xlsx" || fileExtension === "xls") {
					blob = getBlobXlsx(responseFile);
				} else {
					let contentType = "";

					if (fileExtension === "jpg" || fileExtension === "jpeg") {
						contentType = "image/jpeg";
					} else if (fileExtension === "png") {
						contentType = "image/png";
					} else {
						contentType = "image/jpeg";
					}

					blob = getBlobImage(contentType, responseFile);
				}

				downloadFile(blob, fileName);
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: i18next.t("failed_connection"),
			}),
		);
	}

	dispatch(deactivateLoading(module));
}

export async function uploadDocument(dispatch, folderPath, documentName, uploadedFile) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	try {
		let fileToUpload = uploadedFile;
		if (documentName) {
			let extension = uploadedFile.name.substring(uploadedFile.name.lastIndexOf("."));
			fileToUpload = new File([uploadedFile], documentName + extension, {
				type: uploadedFile.type,
			});
		}

		const formData = new FormData();
		formData.append("path", encodeURIComponent(folderPath));
		formData.append("data", fileToUpload);
		let axiosInstance = configManager.getConfig("axiosInstance");
		const response = await axiosInstance.post("fileServerWS/save", formData, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		var result = false;
		if (response["status"] === 200) {
			var ret = response["data"];
			if (ret.retval < 0) {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: i18next.t(ret.messaggio),
					}),
				);
			} else {
				result = true;
			}
		} else {
			dispatch(
				setGlobalError({
					module: module,
					errorMessage: i18next.t("failed_connection"),
				}),
			);
		}
	} catch (ex) {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: ex.code === "ERR_NETWORK" ? i18next.t("failed_connection") : ex.response?.data,
			}),
		);
	}

	dispatch(deactivateLoading(module));

	return result;
}

export async function uploadDocuments(dispatch, folderPath, uploadedFiles) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));

	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	var result = true;
	var errorString = [];

	await Promise.all(
		[...uploadedFiles].map(async (uploadedFile) => {
			try {
				let fileToUpload = uploadedFile;

				const formData = new FormData();
				formData.append("path", encodeURIComponent(folderPath));
				formData.append("data", fileToUpload);
				let axiosInstance = configManager.getConfig("axiosInstance");
				const response = await axiosInstance.post("fileServerWS/save", formData, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});

				if (response["status"] === 200) {
					var ret = response["data"];
					if (ret.retval < 0) {
						errorString.push(uploadedFile.name + ": " + i18next.t(ret.messaggio));
						result = false;
					}
				} else {
					errorString.push(uploadedFile.name + ": " + i18next.t("failed_connection"));
					result = false;
				}
			} catch (ex) {
				errorString.push(uploadedFile.name + ": " + (ex.code === "ERR_NETWORK" ? i18next.t("failed_connection") : ex.response?.data));
				result = false;
			}
		}),
	);

	if (!result) {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: errorString.join("\n"),
			}),
		);
	}

	dispatch(deactivateLoading(module));

	return result;
}

export async function createNewFolderAxios(dispatch, folderPath, folder) {
	const module = localStorage.getItem(configManager.getConfig("localStorageModule"));
	dispatch(activateLoading(module));
	const token = localStorage.getItem(configManager.getConfig("localStorageToken"));
	var result = true;
	var errorString = [];
	let response;
	let axiosInstance = configManager.getConfig("axiosInstance");
	try {
		const formData = new FormData();
		formData.append("path", encodeURIComponent(folderPath));
		formData.append("folderName", encodeURIComponent(folder.name));
		response = await axiosInstance.post("fileServerWS/newdir", formData, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	} catch (ex) {
		errorString.push(ex.code === "ERR_NETWORK" ? i18next.t("failed_connection") : ex.response?.data.messaggio || ex.response?.data);
		result = false;
	}
	if (!result) {
		dispatch(
			setGlobalError({
				module: module,
				errorMessage: errorString.join("\n"),
			}),
		);
	}
	dispatch(deactivateLoading(module));
	return result;
}
